import React, { useEffect, useState } from 'react';

import { UploadPhoto } from './UploadPhoto';
import { SelectedPhotos } from './SelectedPhotos';
import { IPhoto } from 'core/context/consumer/objectDetails';
import { Typography } from '@material-ui/core';
import { PhotoCropper } from './PhotoCropper';
import { FileSize, getPhotoUrl } from 'core';
import { _get } from 'core/api/_utils/api';

export interface PhotoOnCrop {
    id: number;
    photo: string | IPhoto | null;
    existing: boolean | null;
    index?: number;
}

type PhotosProps = {
    initialPhotos?: IPhoto[];
    hasPendingAssessments?: boolean;
    setCurrentPhotos: (photos: any) => void;
    onDelete?: (id: number) => void;
    onRemovePhoto?: (id: number) => void;
};

export const Photos: React.FC<PhotosProps> = ({ setCurrentPhotos, initialPhotos, onDelete }) => {
    const [photos, setPhotos] = useState<Array<string | IPhoto>>(initialPhotos ?? []);
    const [photoOnCrop, setPhotoOnCrop] = useState<PhotoOnCrop>({ id: -1, photo: null, existing: null });

    const addNewPhotos = (newPhotos: string[]): void => {
        setPhotos([...photos, ...newPhotos]);
    };

    useEffect(() => {
        setCurrentPhotos(photos);
    }, [photos]);

    const onDeleteClick = (photo: IPhoto | string, photoIndex: number): void => {
        if (typeof photo === 'string') {
            setPhotos(photos.filter((_p, index) => index !== photoIndex));
        } else {
            setPhotos(
                photos.map((p) => {
                    if ((p as IPhoto).id === photo.id) {
                        return {
                            ...photo,
                            deleted: true,
                        };
                    }
                    return p;
                }),
            );
            if (onDelete) {
                onDelete(photo.id);
            }
        }
    };

    const onCropClick = async (photo: IPhoto | string, index?: number): Promise<void> => {
        if (typeof photo === 'string') {
            setPhotoOnCrop({ id: -1, photo, existing: false, index });
        } else {
            // For existing photos, fetch as base64 to avoid CORS issues
            try {
                const res = await _get(`Photo/GetBase64/${photo.id}`);
                const { data } = res || {};
                if (data?.base64) {
                    const { base64 } = data;
                    const dataUrl = `data:image/png;base64,${base64}`;
                    setPhotoOnCrop({
                        id: photo.id,
                        photo: dataUrl,
                        existing: true,
                    });
                } else {
                    // Fallback to URL if base64 fetch fails
                    setPhotoOnCrop({
                        id: photo.id,
                        photo: getPhotoUrl(photo.url, FileSize.SM) ?? '',
                        existing: true,
                    });
                }
            } catch (error) {
                console.error('Failed to fetch photo as base64:', error);
                // Fallback to URL if fetch fails
                setPhotoOnCrop({
                    id: photo.id,
                    photo: getPhotoUrl(photo.url, FileSize.SM) ?? '',
                    existing: true,
                });
            }
        }
    };

    const onCropCancel = (): void => {
        setPhotoOnCrop({
            id: -1,
            photo: null,
            existing: null,
        });
    };

    const onCropDone = (res: string): void => {
        if (photoOnCrop.index === 0 || photoOnCrop.index) {
            setPhotos(
                photos.map((photo, index) => {
                    if (index === photoOnCrop.index) {
                        return res;
                    }
                    return photo;
                }),
            );
        } else {
            const oldPhoto = photos.filter((photo) => (photo as IPhoto).id === photoOnCrop.id)[0];
            setPhotos([
                ...photos.map((photo) => {
                    if ((photo as IPhoto).id === photoOnCrop.id) {
                        return res;
                    }
                    return photo;
                }),
                { ...(oldPhoto as IPhoto), deleted: true },
            ]);
        }
        setPhotoOnCrop({ id: -1, photo: null, existing: null });
    };

    return (
        <>
            <SelectedPhotos photos={photos} onDeleteClick={onDeleteClick} onCropClick={onCropClick} />
            {photos.length < 25 && !photoOnCrop.photo && (
                <UploadPhoto onUpload={addNewPhotos} photos={photos} />
            )}

            {!!photoOnCrop.photo && (
                <PhotoCropper
                    src={typeof photoOnCrop.photo === 'string' ? photoOnCrop.photo : photoOnCrop.photo.url ?? ''}
                    onCancelClick={onCropCancel}
                    onDoneClick={onCropDone}
                />
            )}

            {photos.length >= 25 && (
                <Typography className="error-message" color={'error'} style={{ marginBottom: 10 }}>
                    You can not upload more than 25 pictures.
                </Typography>
            )}
        </>
    );
};
